import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Stores
import { devices_settings } from 'src/stores';
//Helpers
import { capFirstChar } from 'src/helpers/utilities';
import {
	CAMERA_PLAY_SOUND_METHOD,
	CAMERA_ZONE_SET_CONFIG_METHOD,
	getCameraActionFlags,
	isCameraArmPrivacyMethod,
} from '../devices/camera_local_actions';
import { getCameraSoundIcon, getCameraSoundLabel } from 'src/components/Camera/helpers/camera-sounds';
//Types
import type { Device } from 'src/types/Device';
import type { LabelProps } from '@Shelly/shelly-design-system/index';
import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

const getCameraZoneLabel = (deviceId: string, zoneId?: number): string => {
	const $format = get(format);
	const zone = devices_settings.getByRealId(deviceId)?.[`camerazone:${zoneId}`];

	return (
		capFirstChar(zone?.name || '') ||
		`${$format('scene_camera_action_zone', {
			default: 'Camera zone',
		})} (${zoneId})`
	);
};

export const buildCameraLabels = (
	method: string,
	job: ActionJob,
	labelList: LabelProps[],
	deviceId = '',
): LabelProps[] => {
	if (isCameraArmPrivacyMethod(method)) {
		const $format = get(format);
		const { arm, privacy } = getCameraActionFlags(job);
		if (arm != null) {
			labelList.push({
				value: arm
					? $format('camera_motion_detection_armed', { default: 'Armed' })
					: $format('camera_motion_detection_disarmed', { default: 'Disarmed' }),
				icon: arm ? 'fas fa-shield' : 'fas fa-shield-halved',
			});
		}
		if (privacy != null) {
			labelList.push({
				value: privacy
					? $format('camera_privacy_mode_on', { default: 'Privacy On' })
					: $format('camera_privacy_mode_off', { default: 'Privacy Off' }),
				icon: privacy ? 'fas fa-video-slash' : 'fas fa-video',
			});
		}
	} else if (method === CAMERA_PLAY_SOUND_METHOD) {
		labelList.push({
			value: stringifyCameraAction(method, job, deviceId),
			icon: getCameraSoundIcon(job.params?.sound),
		});
	} else if (method === CAMERA_ZONE_SET_CONFIG_METHOD) {
		const $format = get(format);
		labelList.push({
			value: getCameraZoneLabel(deviceId, job.params?.id),
			icon: 'fas fa-vector-square',
		});
		if (isCameraZoneDeleted(deviceId, job.params?.id)) {
			labelList.push({
				value: $format('camera_zone_deleted_alert', {
					default: 'Camera zone was deleted',
				}),
				icon: 'fas fa-triangle-exclamation',
				iconColor: '--color-warning',
			});
		}
		if (job.params?.config?.enable != null) {
			labelList.push({
				value: job.params.config.enable
					? $format('camera_zone_enable_action', { default: 'Enable zone' })
					: $format('camera_zone_disable_action', { default: 'Disable zone' }),
				icon: job.params.config.enable ? 'fas fa-circle-check' : 'fas fa-circle-xmark',
			});
		}
	} else {
		labelList.push({
			value: stringifyCameraAction(method, null, deviceId),
			icon: getLocalActionIcons(method),
		});
	}
	if (method === 'camera.startrecording' && job.params?.duration != null) {
		labelList.push({
			value: `${job.params.duration}s`,
			icon: 'fas fa-timer',
		});
	}
	return labelList;
};

export const stringifyCameraAction = (method: string, action?: ActionJob, deviceId = ''): string => {
	const $format = get(format);
	let actionStringify = '';
	if (method === 'camera.captureimage') {
		actionStringify = $format('local_action_camera_capture_image', { default: 'Capture Image' });
	} else if (method === 'camera.startrecording') {
		actionStringify = $format('local_action_camera_start_recording', { default: 'Start Recording' });
		if (action?.params?.duration != null) {
			actionStringify += ` ${$format('text_trv_setting_duration', {
				default: 'Duration',
			})}: ${action.params.duration} ${$format('timer_text_sec', { default: 'sec' })}.`;
		}
	} else if (method === CAMERA_PLAY_SOUND_METHOD) {
		actionStringify = $format('command_camera_play_sound', {
			default: 'Play sound: {sound}',
			values: { sound: getCameraSoundLabel(action?.params?.sound) },
		});
	} else if (isCameraArmPrivacyMethod(method)) {
		const { arm, privacy } = getCameraActionFlags(action);
		if (arm != null) {
			actionStringify = arm
				? $format('camera_motion_detection_armed', { default: 'Armed' })
				: $format('camera_motion_detection_disarmed', { default: 'Disarmed' });
		} else if (privacy != null) {
			actionStringify = privacy
				? $format('camera_privacy_mode_on', { default: 'Privacy On' })
				: $format('camera_privacy_mode_off', { default: 'Privacy Off' });
		}
		if (!actionStringify) {
			actionStringify = $format('local_action_camera_configure', {
				default: 'Configure Camera',
			});
		}
	} else if (method === CAMERA_ZONE_SET_CONFIG_METHOD) {
		actionStringify = getCameraZoneLabel(deviceId, action?.params?.id);
		if (action?.params?.config?.enable != null) {
			actionStringify += `: ${
				action.params.config.enable
					? $format('camera_zone_enable_action', { default: 'Enable zone' })
					: $format('camera_zone_disable_action', { default: 'Disable zone' })
			}`;
		}
	}
	return actionStringify;
};

export const showDeletedCameraZoneAlert = (device: Device, action: ActionJob): boolean => {
	return (
		action?.method?.toLowerCase() === CAMERA_ZONE_SET_CONFIG_METHOD &&
		isCameraZoneDeleted(device.id, action.params?.id)
	);
};

const getLocalActionIcons = (method: string): string => {
	if (method === 'camera.startrecording') return 'fas fa-circle-dot';
	if (method === CAMERA_ZONE_SET_CONFIG_METHOD) return 'fas fa-vector-square';
	return 'fas fa-camera';
};

const isCameraZoneDeleted = (deviceId: string, zoneId?: number): boolean => {
	return zoneId != null && !devices_settings.getByRealId(deviceId)?.[`camerazone:${zoneId}`];
};
